"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Fingerprint, Sparkles, Zap, ArrowRight, Shield, MessageSquare, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Link } from '@/components/common/Link';
import { cn } from '@/lib/utils';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  id: 1,
  title: "Friday Mixer",
  description: "Kick off the weekend at the Skyline Lounge. Reconnect with old friends over cocktails and classic hits from 2005. Dress code: Casual Chic.",
  icon: MessageSquare
}, {
  id: 2,
  title: "Saturday Gala",
  description: "The main event in the Grand Ballroom. A formal dinner, awards ceremony, and dancing under the stars. Dress code: Black Tie Optional.",
  icon: Sparkles
}, {
  id: 3,
  title: "Sunday Brunch",
  description: "A relaxed farewell gathering at the Alumni Garden. Share final memories and exchange contacts before heading home. Dress code: Garden Attire.",
  icon: Zap
}];
function RevealWrapper({
  children,
  delay = 0,
  className
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  return <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-16 scale-95", className)} style={{
    transitionDelay: `${delay}ms`
  }}>
            {children}
        </div>;
}
export default function TheMainEventMore() {
  return <section data-section-id="263415" id="the-main-event-more" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
            {}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-lg blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-primary/5 rounded-lg blur-[150px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <h2 className="font-default text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                        The Weekend <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent drop-shadow-[0_0_15px_rgba(var(--color-primary),0.3)]">Agenda</span>
                    </h2>
                    <p className="text-lg text-muted-foreground font-default">
                        Three days of nostalgia, celebration, and reconnection. Mark your calendars for the ultimate Skyline High 20-year milestone.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-8 items-center">
                    
                    {}
                    <div className="relative">
                        {}
                        <div className="absolute left-[2.25rem] top-8 bottom-8 w-px bg-gradient-to-b from-primary/0 via-primary/50 to-primary/0 hidden sm:block" />

                        <div className="space-y-12 sm:space-y-16">
                            {steps.map((step, i) => <div data-index={i} key={step.id} className="relative flex flex-col sm:flex-row gap-6 sm:gap-8 group">
                                    {}
                                    <div className="relative z-10 flex-shrink-0">
                                        <div className="w-16 h-16 rounded-md bg-secondary/40 backdrop-blur-xl border border-primary/20 flex items-center justify-center shadow-md shadow-primary/5 group-hover:shadow-primary/20 group-hover:border-primary/50 transition-all duration-500 relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            <span className="font-default text-2xl font-bold text-primary drop-shadow-[0_0_10px_rgba(var(--color-primary),0.8)]">
                                                0{step.id}
                                            </span>
                                        </div>
                                    </div>

                                    {}
                                    <div className="pt-2">
                                        <h3 className="font-default text-2xl font-semibold mb-3 text-foreground flex items-center gap-3">
                                            {step.title}
                                            <step.icon className="w-5 h-5 text-primary/60" />
                                        </h3>
                                        <p className="text-muted-foreground leading-relaxed font-default">
                                            {step.description}
                                        </p>
                                    </div>
                                </div>)}
                        </div>

                        <div className="mt-16 sm:ml-24">
                            <Button size="lg" className="rounded-md bg-gradient-to-r from-primary to-accent hover:shadow-[0_0_30px_-5px_var(--color-primary)] transition-all duration-300" asChild>
                                <Link to="/TheWeekendAgendaPage">
                                    View Full Schedule
                                    <ArrowRight className="ml-2 w-4 h-4" />
                                </Link>
                            </Button>
                        </div>
                    </div>

                    {}
                    <div className="relative h-[600px] hidden lg:block perspective-[2000px]">
                        
                        {}
                        <RevealWrapper delay={100} className="absolute top-0 right-12 z-10 transform rotate-y-[-10deg] rotate-x-[5deg]">
                            <Card className="w-72 bg-secondary/30 backdrop-blur-2xl border-primary/20 shadow-2xl shadow-black/50 overflow-hidden rounded-md">
                                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
                                <CardHeader className="pb-4 border-b border-primary/10">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-md bg-primary/10 mx-auto mb-4">
                                        <Shield className="w-6 h-6 text-primary drop-shadow-[0_0_8px_rgba(var(--color-primary),0.5)]" />
                                    </div>
                                    <CardTitle className="text-center text-lg font-default font-medium">RSVP Status</CardTitle>
                                </CardHeader>
                                <CardContent className="pt-6 space-y-4">
                                    <div className="h-12 rounded-md bg-background/50 border border-primary/10 flex items-center justify-center gap-3 shadow-inner">
                                        <div className="w-5 h-5 rounded-md bg-foreground/20" />
                                        <div className="h-4 w-24 bg-foreground/40 rounded-md" />
                                    </div>
                                    <div className="h-12 rounded-md bg-primary/20 border border-primary/30 flex items-center justify-center">
                                        <div className="h-4 w-32 bg-primary rounded-md" />
                                    </div>
                                </CardContent>
                            </Card>
                        </RevealWrapper>

                        {}
                        <RevealWrapper delay={300} className="absolute top-1/3 left-0 z-20 transform rotate-y-[5deg] rotate-x-[-5deg]">
                            <Card className="w-80 bg-secondary/40 backdrop-blur-3xl border-primary/20 shadow-2xl shadow-black/60 overflow-hidden rounded-md">
                                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
                                <CardHeader className="pb-4 flex flex-row items-center gap-4 border-b border-primary/10">
                                    <div className="w-10 h-10 rounded-md bg-accent/20 flex items-center justify-center">
                                        <Sparkles className="w-5 h-5 text-accent" />
                                    </div>
                                    <div>
                                        <div className="h-4 w-24 bg-foreground/80 rounded-md mb-2" />
                                        <div className="h-3 w-32 bg-muted-foreground/50 rounded-md" />
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6 space-y-5">
                                    {[1, 2, 3].map((_, i) => <div data-index={i} key={i} className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-md bg-background/50 border border-primary/10" />
                                                <div className="h-3 w-20 bg-foreground/60 rounded-md" />
                                            </div>
                                            <div className="w-10 h-5 rounded-md bg-primary/30 border border-primary/20 relative">
                                                <div className="absolute right-1 top-0.5 w-4 h-4 rounded-md bg-primary" />
                                            </div>
                                        </div>)}
                                </CardContent>
                            </Card>
                        </RevealWrapper>

                        {}
                        <RevealWrapper delay={500} className="absolute bottom-0 right-4 z-30 transform rotate-y-[-15deg] rotate-x-[10deg]">
                            <Card className="w-72 bg-secondary/50 backdrop-blur-3xl border-primary/20 shadow-3xl shadow-black/70 overflow-hidden rounded-md">
                                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
                                <CardHeader className="py-4 border-b border-primary/10 bg-background/20">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-md bg-gradient-to-tr from-primary to-accent" />
                                        <div>
                                            <div className="h-3 w-20 bg-foreground/90 rounded-md mb-1.5" />
                                            <div className="h-2 w-12 bg-primary/80 rounded-md" />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-4 pb-4 space-y-4">
                                    <div className="flex gap-3">
                                        <div className="w-6 h-6 rounded-md bg-muted-foreground/20 shrink-0 mt-1" />
                                        <div className="bg-background/50 border border-primary/10 p-3 rounded-md rounded-tl-sm">
                                            <div className="h-2 w-32 bg-foreground/70 rounded-md mb-2" />
                                            <div className="h-2 w-24 bg-foreground/70 rounded-md" />
                                        </div>
                                    </div>
                                    <div className="flex gap-3 flex-row-reverse">
                                        <div className="bg-primary/20 border border-primary/20 p-3 rounded-md rounded-tr-sm">
                                            <div className="h-2 w-28 bg-primary/90 rounded-md mb-2" />
                                            <div className="h-2 w-16 bg-primary/90 rounded-md" />
                                        </div>
                                    </div>
                                </CardContent>
                                <div className="p-3 border-t border-primary/10 bg-background/30 flex gap-2">
                                    <div className="h-8 flex-1 bg-background/50 rounded-md border border-primary/10" />
                                    <div className="w-8 h-8 rounded-md bg-primary/20 flex items-center justify-center">
                                        <MessageSquare className="w-4 h-4 text-primary" />
                                    </div>
                                </div>
                            </Card>
                        </RevealWrapper>

                    </div>
                </div>
            </div>
        </section>;
}